























































































































































































import {computed, defineComponent, PropType, ref} from "@vue/composition-api";
import Breadcrumbs from "@tool/Components/Breadcrumbs.vue";
import ThemeBlock from "@base/Components/theme/ThemeBlock.vue";
import {TeamEntityInterface} from "@tool/tool.types";
import AppTeamLayout from "@tool/Pages/App/Teams/AppTeamLayout.vue";
import moment from "moment";
import {
    BillingInterval,
    CustomSubscriptionEntityInterface,
    DefinedBillingPlan,
    PaddleJsInterface,
    PaddleSubscriptionEntityInterface,
    PaddleSubscriptionInfoInterface,
    ReceiptEntityInterface
} from "@tool/Pages/App/Teams/Billing/billing.types";
import {formatPrice} from "@tool/Pages/App/Teams/Billing/billing.helpers";
import {FEATURES} from "@tool/Pages/App/Teams/Billing/FEATURES";
import PaidPlanFeature from "@tool/Pages/App/Teams/Billing/partials/PaidPlanFeature.vue";
import useSwal from "@tool/Helpers/useSwal";
import useAxios, {axiosErrorTransformer} from "@tool/Helpers/useAxios";
import useAppStore from "@tool/store/useAppStore";
import {useInertia} from "@tool/Helpers/useInertia";
import QuantitySelector from "@tool/Pages/App/Teams/Billing/partials/QuantitySelector.vue";
import SubscribeButtons from "@tool/Pages/App/Teams/Billing/partials/SubscribeButtons.vue";
import {WithProps} from "@base/base.types";

export default defineComponent({
    components: {
        AppTeamLayout,
        Breadcrumbs,
        ThemeBlock,
        PaidPlanFeature,
        QuantitySelector,
        SubscribeButtons
    },
    props: {
        team: {
            type: Object as PropType<WithProps<TeamEntityInterface, 'projects_count'>>,
            required: true
        },
        plans: {
            type: Array as PropType<DefinedBillingPlan[]>,
            required: true
        },
        receipts: {
            type: Array as PropType<ReceiptEntityInterface[]>,
            required: true
        },
        currentValidCustomSubscription: {
            type: Object as PropType<CustomSubscriptionEntityInterface>,
            required: false
        },
        currentValidPaddleSubscription: {
            type: Object as PropType<PaddleSubscriptionEntityInterface>,
            required: false
        },
        paddleAuth: {
            type: Object as PropType<{ vendor_id: number; sandbox: boolean }>,
            required: true,
        },
    },
    setup(props) {

        const {loadingOverlay} = useAppStore();

        /**
         * Paddle
         */
        const paddle = ref<null | PaddleJsInterface>(null);

        const initPaddle = () => {
            let paddleScript = document.createElement('script');
            paddleScript.setAttribute('src', 'https://cdn.paddle.com/paddle/paddle.js');
            paddleScript.onload = () => {
                paddle.value = (window as any).Paddle as PaddleJsInterface;
                if (props.paddleAuth.sandbox) {
                    paddle.value.Environment.set('sandbox');
                }
                paddle.value.Setup({
                    vendor: props.paddleAuth.vendor_id
                });
            };

            document.head.appendChild(paddleScript);
        }

        initPaddle();

        /**
         * Next payment info is unreliable
         */
        const paddleSubscriptionInfo = ref<null | PaddleSubscriptionInfoInterface>(null);
        const loadPaddleInfo = async () => {
            if (!props.currentValidPaddleSubscription) {
                return;
            }
            const response = await useAxios().get<PaddleSubscriptionInfoInterface>(
                    route('app.team.billing.subscription.paddle-info', {
                        tool_team_entity: props.team.id,
                        subscription_id: props.currentValidPaddleSubscription.id
                    })
            );
            paddleSubscriptionInfo.value = response.data;
        }
        loadPaddleInfo();

        const planTitle = props.team.billing_trial_ends_at
                ? 'Pro (Free Trial)' :
                (props.currentValidPaddleSubscription
                                ? 'Pro - ' + props.currentValidPaddleSubscription.quantity + ' Projects (' + props.currentValidPaddleSubscription.interval + ')'
                                : 'Free'
                );

        const subscribeQuantityMinimum = computed(() => {
            const plan = props.plans.sort((a, b) => a.quantity < b.quantity ? -1 : a.quantity === b.quantity ? 0 : 1)
                    .find(plan => plan.quantity > props.team.projects_count);

            return plan!.quantity;
        });

        const checkIfSubscriptionIsNotPendingAnymore = async () => {
            const response = await useAxios().get<{ pending: boolean }>(route('app.team.billing.pending-checkout.get', props.team.id));

            if (response.data.pending === false) {
                useInertia().get(route(route().current(), route().params), {}, {preserveScroll: true});
                return;
            }

            setTimeout(() => checkIfSubscriptionIsNotPendingAnymore(), 1000);

        }

        /**
         * Shared
         */
        const planQuantityOptions = props.plans.map(plan => plan.quantity);

        const helperGetPlanPriceForInterval = (plan: DefinedBillingPlan, interval: BillingInterval) => {
            return interval === 'monthly' ? plan.price_monthly : plan.price_yearly;
        }

        const helperDisplayAbsoluteAndRelativeDate = (date: string) => {
            const momentDate = moment(date);
            const days = momentDate.diff(moment(), 'days');

            return `${momentDate.format('MMMM Do, YYYY')} (in ${days} days)`;
        }
        const helperDisplayPaddleConnectionErrorSwal = () => {
            useSwal().fire({
                title: 'Connection problems!',
                text: 'It seems like there was a connection error with our payment provider. Please try again and make sure your internet connection is strong.',
                icon: 'error'
            });
        }

        const helperValidatePaddleIsAvailable: () => (boolean) = () => {
            if (!paddle) {
                helperDisplayPaddleConnectionErrorSwal();

                return false;
            }

            return true;
        }
        const helperShowSuccessfulPlanChangeSwal = () => {
            useSwal().fire({
                title: 'Plan changed!',
                text: 'Your plan was successfully changed!',
                icon: 'success'
            })
        }

        /**
         * Value is going to be updated from the QuantitySelector component
         */
        const subscribeForm = ref({
            quantity: props.plans[0].quantity
        });

        const modifyForm = ref({
            quantity: props.plans[0].quantity
        });

        const findPlanByQuantity: (quantity: number) => DefinedBillingPlan = (quantity: number) => {
            const plan = props.plans.find(plan => plan.quantity === quantity);

            if (!plan) {
                throw new Error('Could not find plan with quantity: ' + quantity);
            }

            return plan;
        }

        const subscribeSelectedPlan = computed<DefinedBillingPlan>(
                () => findPlanByQuantity(subscribeForm.value.quantity)
        );

        const modifySelectedPlan = computed<DefinedBillingPlan>(
                () => findPlanByQuantity(modifyForm.value.quantity)
        );

        const subscribeSubmit = async (interval: 'monthly' | 'yearly', plan: DefinedBillingPlan) => {

            if (!helperValidatePaddleIsAvailable()) {
                return;
            }

            loadingOverlay.value = true;

            try {
                const response = await useAxios().post<{ link: string }>(
                        route('app.team.billing.generate-pay-link', props.team.id),
                        {
                            quantity: plan.quantity,
                            interval,
                            price: helperGetPlanPriceForInterval(plan, interval)
                        }
                );

                loadingOverlay.value = true;

                paddle.value!.Checkout.open({
                    override: response.data.link,
                    successCallback: (data) => {
                        useAxios().put(route('app.team.billing.pending-checkout.update', props.team.id), {
                            checkout_id: data.checkout.id
                        }).then(() => {
                            loadingOverlay.value = true;
                            useInertia().get(route(route().current(), route().params), {}, {
                                onSuccess() {
                                    checkIfSubscriptionIsNotPendingAnymore();
                                    loadingOverlay.value = false;
                                }
                            });
                        })
                    },
                    closeCallback: () => {
                        loadingOverlay.value = false;
                    }
                })


            } catch (e) {
                helperDisplayPaddleConnectionErrorSwal();
            }

            loadingOverlay.value = false;
        }

        /**
         * @param interval
         * @param plan
         */

        const modifySubmit = async (interval: 'monthly' | 'yearly', plan: DefinedBillingPlan) => {

            useSwal().fire({
                title: 'Confirm this action!',
                text: 'Please confirm to proceed and change your plan.',
                confirmButtonText: 'Confirm',
                cancelButtonText: 'Cancel',
                showCancelButton: true,
                icon: 'warning',
                preConfirm() {
                    useSwal().showLoading();
                    return useAxios().put<{ success: true }>(route('app.team.billing.subscription.update', {
                        tool_team_entity: props.team.id,
                        subscription_id: props.currentValidPaddleSubscription!.id
                    }), {
                        interval,
                        quantity: plan.quantity,
                        price: helperGetPlanPriceForInterval(plan, interval)
                    })
                            .then(() => {
                                return new Promise(resolve => {
                                    useInertia().get(route(route().current(), route().params), {}, {
                                        onSuccess() {
                                            helperShowSuccessfulPlanChangeSwal();
                                            resolve(true);
                                        }
                                    });
                                })

                            })
                            .catch(error => {
                                const axiosError = axiosErrorTransformer(error);
                                useSwal().showValidationMessage(axiosError.message);
                            });
                }
            });
        }

        const reactivateSubscriptionSubmit = async () => {
            useSwal().fire({
                title: 'Confirm this action!',
                text: 'Please confirm to proceed and reactivate your subscription.',
                confirmButtonText: 'Confirm',
                cancelButtonText: 'Cancel',
                showCancelButton: true,
                icon: 'warning',
                preConfirm() {
                    useSwal().showLoading();

                    return useAxios().post<{ success: true }>(route('app.team.billing.subscription.reactivate', {
                        tool_team_entity: props.team.id,
                        subscription_id: props.currentValidPaddleSubscription!.id
                    }))
                            .then(() => {
                                return new Promise(resolve => {
                                    useInertia().get(route(route().current(), route().params), {}, {
                                        onSuccess() {
                                            useSwal().fire({
                                                title: 'Subscription reactivated!',
                                                text: 'Your subscription was successfully reactivated!',
                                                icon: 'success'
                                            })
                                            resolve(true);
                                        }
                                    });
                                })

                            })
                            .catch(error => {
                                const axiosError = axiosErrorTransformer(error);
                                useSwal().showValidationMessage(axiosError.message)
                            });
                }
            })
        }

        const actionSwitchToYearlyPlan = async () => {
            useSwal().fire({
                title: 'Confirm this action!',
                text: 'Please confirm to proceed and switch to a yearly plan.',
                confirmButtonText: 'Confirm',
                cancelButtonText: 'Cancel',
                showCancelButton: true,
                icon: 'warning',
                preConfirm() {
                    useSwal().showLoading();
                    return useAxios().post<{ success: true }>(route('app.team.billing.subscription.switch-to-yearly', {
                        tool_team_entity: props.team.id,
                        subscription_id: props.currentValidPaddleSubscription!.id
                    }))
                            .then(() => {
                                return new Promise(resolve => {
                                    useInertia().get(route(route().current(), route().params), {}, {
                                        onSuccess() {
                                            helperShowSuccessfulPlanChangeSwal();
                                            resolve(true);
                                        }
                                    });
                                })

                            })
                            .catch(error => {
                                const axiosError = axiosErrorTransformer(error);
                                useSwal().showValidationMessage(axiosError.message);
                            });
                }
            })
        };

        const updatePaymentMethodSubmit = async (update_url: string) => {
            if (!helperValidatePaddleIsAvailable()) {
                return;
            }

            loadingOverlay.value = true;
            paddle.value!.Checkout.open({
                override: update_url,
                successCallback(data) {
                    useInertia().get(route(route().current(), route().params), {}, {preserveScroll: true});
                },
                loadCallback() {
                    loadingOverlay.value = false;
                }
            })
        }

        const cancelModal = ref(false);

        const cancelSubmit = async () => {
            loadingOverlay.value = true;

            useAxios().delete(route('app.team.billing.subscription.cancel', {
                tool_team_entity: props.team.id,
                subscription_id: props.currentValidPaddleSubscription!.id
            }))
                    .then(() => {
                        useInertia().get(route(route().current(), route().params), {}, {
                            onSuccess() {
                                useSwal().fire({
                                    title: 'Cancelled!',
                                    text: 'Your subscription was successfully cancelled! If you change your mind, you can always subscribe again!',
                                    icon: 'success'
                                })
                            },
                            onFinish() {
                                loadingOverlay.value = false;
                            }
                        });
                    }).catch((e) => {
                loadingOverlay.value = false;
                useSwal().fire({
                    title: e.response && e.response.data ? e.response.data.message : e.message,
                });
            })
        }

        const openChatWindow = () => {

            try {
                (window as any).openChatWidget();
            } catch (e) {
                useSwal().fire({
                    text: 'Could not load chat plugin. Please try again later, or send an email to support@utmtool.com.',
                    icon: 'error'
                });
            }

        }


        return {
            planTitle,
            paddleSubscriptionInfo,

            // Shared
            subscribeQuantityMinimum,

            // Subscribe
            planQuantityOptions,
            subscribeForm,
            subscribeSelectedPlan,
            subscribeSubmit,
            // Modify
            modifyForm,
            modifySelectedPlan,
            modifySubmit,

            // Update Payment Method
            updatePaymentMethodSubmit,

            // Reactivate
            reactivateSubscriptionSubmit,

            // Cancel
            cancelModal,
            cancelSubmit,


            helperDisplayAbsoluteAndRelativeDate,
            formatPrice,
            moment,
            paddle,
            FEATURES,

            actionSwitchToYearlyPlan,
            openChatWindow,

        }
    }
})
